import { type ReactNode } from 'react'
import Highlighter from 'react-highlight-words'
import classnames from 'classnames'
import {
  Avatar,
  Box,
  CellWithoutContent,
  DELETED_CONTACT_AVATAR_INFO,
  EmptyCell,
  type IAvatarInfo,
  IconButton,
  type ITableConfig,
  ItemsWithCount,
  Tooltip,
  Typography,
} from 'shared/ui'
import { getLabelAsNumberInternationalFormat } from 'shared/lib'
import { TagCard } from 'entities/Tags'
import { TrialContactsLimitTooltip } from 'entities/Subscription/ui/TrialContactsLimitTooltip'
import { type RowContact } from 'entities/Contacts/model/Table/RowContact'
import { type Tag } from 'entities/Tags/model/Tag'
import { ConversationCall } from 'entities/Conversation/actions/ConversationCall/ConversationCall'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import styles from './styles.module.scss'

type IGetCellForName = {
  row: {
    id: number
    isDisabled: boolean
    avatarInfo: IAvatarInfo
    full_name: string
    isArchived?: boolean
  }
  onOpenContactsDetails: IOpenContactsDetails
  config?: ITableConfig
  inboxId?: number
  messageId?: number
  size?: 'large'
  search?: string
  onClickOpenConversation?: () => void
  conversationCallInfo?: {
    inboxId: number
    conversationId: number
  }
}

export const getCellForName = ({
  row,
  onOpenContactsDetails,
  config,
  inboxId,
  messageId,
  size,
  search,
  onClickOpenConversation,
  conversationCallInfo,
}: IGetCellForName) => {
  const contact = row
  const newContact = contact.id === -1
  const showSendMessageBubble = !newContact && !row.isDisabled && !config?.hideSendMessage

  const height = size === 'large' ? '56px' : '32px'
  const avatarSize = size === 'large' ? 32 : 24
  const gap = size === 'large' ? 1.5 : 1

  const number = contact.avatarInfo.number
    ? getLabelAsNumberInternationalFormat(contact.avatarInfo.number)
    : undefined

  return (
    <Box display='flex' justifyContent='space-between' width={'100%'} alignItems={'center'} gap={1}>
      <Box
        sx={{
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          cursor: row.isDisabled ? 'default' : 'pointer',
          height,
        }}
        gap={gap}
        display='flex'
      >
        <TrialContactsLimitTooltip show={contact.isDisabled} />
        {!contact.isDisabled && <Avatar info={contact.avatarInfo} size={avatarSize} />}
        {contact.isArchived && <Avatar info={DELETED_CONTACT_AVATAR_INFO} size={avatarSize} />}

        {size === 'large' ? (
          <Box display='grid' gap={0.5}>
            {contact.full_name ? (
              <Typography
                pointer={!row.isDisabled}
                ellipsis
                variant={'body-md-medium'}
                tag={'div'}
                ariaLabel={'fullName'}
                dataHighlightId={'Contact Name'}
              >
                {search ? (
                  <Highlighter
                    highlightClassName={classnames(
                      styles.highlighter,
                      styles['highlighter--label']
                    )}
                    searchWords={search.split(' ')}
                    autoEscape={true}
                    textToHighlight={contact.full_name}
                  />
                ) : (
                  contact.full_name
                )}
              </Typography>
            ) : (
              <EmptyCell />
            )}
            {size === 'large' && number ? (
              <Typography
                pointer={!row.isDisabled}
                ellipsis
                variant={'body-md-regular'}
                tag={'div'}
                ariaLabel={'phoneNumber'}
                dataHighlightId={'Phone Number'}
              >
                {search ? (
                  <Highlighter
                    highlightClassName={classnames(
                      styles.highlighter,
                      styles['highlighter--label']
                    )}
                    searchWords={search.split(' ')}
                    autoEscape={true}
                    textToHighlight={number}
                  />
                ) : (
                  number
                )}
              </Typography>
            ) : (
              <EmptyCell />
            )}
          </Box>
        ) : contact.full_name ? (
          <Typography
            pointer={!row.isDisabled}
            ellipsis
            variant={'body-md-regular'}
            tag={'div'}
            ariaLabel={'formattedValue'}
          >
            {contact.full_name}
          </Typography>
        ) : (
          <EmptyCell />
        )}
      </Box>

      {showSendMessageBubble && (
        <Tooltip label='Conversation' placement='top'>
          <IconButton
            icon='conversation'
            variant='icon'
            color='tertiary'
            transparentInactive
            className={config?.hidden || ''}
            disabled={onOpenContactsDetails.disabled}
            onClick={(event) => {
              event.stopPropagation()
              onOpenContactsDetails.open({
                contactId: row.id,
                inboxId,
                messageId,
              })
              onClickOpenConversation?.()
            }}
            ariaLabel='ContactRow_sendMessage'
          />
        </Tooltip>
      )}
      {conversationCallInfo && (
        <ConversationCall className={config?.hidden || ''} {...conversationCallInfo} />
      )}
    </Box>
  )
}

export const getCellForTags = (row: { tags: Tag[]; isDisabled: boolean }) => {
  if (!row.tags.length) {
    return <EmptyCell />
  }

  return (
    <ItemsWithCount
      items={row.tags}
      itemComponent={({ item }) => <TagCard tag={item} />}
      containerWidth={362}
      width={164}
      disabled={row.isDisabled}
      cacheScope={'tags'}
    />
  )
}

export const getCellNumber = (row: RowContact): ReactNode => {
  if (row.id === -1) {
    return <CellWithoutContent />
  }

  if (!row.origin.number) return null

  return getLabelAsNumberInternationalFormat(row.origin.number)
}
